<template>
  <div>
    <LoadingSpinner :isLoading="loading" />
    <div v-if="notFound">
      <SoNote tone="muted" icon="lock">{{ $t("securone.event.not_found") }}</SoNote>
      <router-link :to="{ name: 'securone_events' }" class="sd-btn sd-btn--secondary inline-flex mt-4"><BaseIcon icon="chevron-left" class="mr-1" />{{ $t("securone.event.back_to_events") }}</router-link>
    </div>

    <!-- ================= running (D-37 sender / D-38 recipient) ================= -->
    <div v-else-if="event && !resolved" class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <router-link :to="{ name: 'securone_events' }" class="text-sm text-securdia-blue hover:underline"><BaseIcon icon="chevron-left" class="mr-1" />{{ $t("securone.event.back_to_events") }}</router-link>
        <span class="text-xs text-secondary-400">{{ event.departmentName }}</span>
      </div>

      <!-- desktop: the event on the left, the chat open on the right; stacked on narrow screens -->
      <div class="grid grid-cols-1 xl:grid-cols-5 gap-4 items-start">
      <div class="xl:col-span-3 flex flex-col gap-4">
      <div class="rounded overflow-hidden border border-secondary-200 border-l-4 border-l-red-600">
        <div class="bg-white px-5 py-4 border-b border-secondary-200">
          <div class="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-red-700">
            <span><span class="so-livedot mr-2"></span>{{ $t("securone.event.running_label") }}</span>
            <span class="font-mono text-sm normal-case tracking-normal text-secondary-900">{{ elapsedSince(event.raisedAt || event.createdAt) }}</span>
          </div>
          <div class="text-2xl font-semibold text-secondary-900 mt-2">{{ event.category.name }}</div>
          <div class="text-sm text-secondary-800 mt-1">{{ event.category.instruction }}</div>
          <div class="inline-flex items-center gap-2 rounded border border-secondary-200 bg-secondary-50 px-2.5 py-1 text-xs text-secondary-800 mt-3"><BaseIcon icon="broadcast-tower" />{{ meInfo.isSender ? $t("securone.event.sent_by_you") : $t("securone.events.sent_by", { name: event.raisedByName }) }}</div>
        </div>

        <div class="bg-white p-4 flex flex-col gap-3">
          <!-- recipient on the desktop: acknowledge = "seen", nothing more (no code) -->
          <div v-if="meInfo.canAcknowledge" class="flex flex-wrap items-center gap-4 rounded bg-secondary-50 border border-secondary-200 px-4 py-3">
            <div class="flex-1 text-xs text-secondary-600 leading-relaxed" style="min-width: 220px">{{ $t("securone.event.acknowledge_help") }}</div>
            <button class="sd-btn sd-btn--danger" :disabled="acking" @click="acknowledge"><BaseIcon icon="check" class="mr-1" />{{ $t("securone.event.acknowledge") }}</button>
          </div>
          <div v-else-if="acknowledged" class="text-xs text-secondary-500 px-1">{{ $t("securone.event.you_acknowledged_at", { time: clock(myAck.acknowledgedAt) }) }}</div>

          <div class="grid grid-cols-2 gap-3">
            <div class="rounded bg-secondary-50 border border-secondary-200 px-4 py-3">
              <div class="text-xs text-secondary-500">{{ $t("securone.event.recipients") }}</div>
              <div class="text-2xl font-semibold text-secondary-800">{{ event.recipientCount }}<span v-if="event.smsRecipientCount" class="text-sm text-secondary-500 font-normal"> + {{ event.smsRecipientCount }} SMS</span></div>
              <div class="text-xs text-secondary-500">{{ $tc("securone.event.groups_count", groups.length, { n: groups.length }) }}</div>
            </div>
            <div class="rounded bg-green-50 border border-green-200 px-4 py-3">
              <div class="text-xs text-green-700">{{ $t("securone.ack.acknowledged") }}</div>
              <div class="text-2xl font-semibold text-green-800">{{ event.acknowledgedCount }}</div>
              <div class="text-xs text-green-700">{{ event.lastAcknowledgedAt ? $t("securone.event.latest_at", { time: clock(event.lastAcknowledgedAt) }) : $t("securone.event.none_yet") }}</div>
            </div>
          </div>

          <button class="w-full flex items-center gap-3 rounded bg-secondary-50 border border-secondary-200 px-4 py-3 text-left hover:bg-secondary-100 focus:outline-none" @click="showAcks = !showAcks">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold text-secondary-800">{{ $t("securone.event.see_acknowledged") }}</div>
              <div class="text-xs text-secondary-500 truncate">{{ ackPreview }}</div>
            </div>
            <BaseIcon :icon="showAcks ? 'chevron-up' : 'chevron-right'" class="text-secondary-400" />
          </button>
          <AckList v-if="showAcks" :acknowledgements="ackRows" :recipientCount="event.recipientCount" />

          <div v-for="d in documents" :key="d._id" class="flex items-center gap-3 rounded bg-secondary-50 border border-secondary-200 px-4 py-3">
            <div class="w-9 h-9 rounded bg-white border border-secondary-200 flex items-center justify-center text-secondary-600"><BaseIcon icon="file-alt" /></div>
            <div class="flex-1 min-w-0">
              <button class="text-sm font-semibold text-securdia-blue hover:underline truncate" @click="openDocument(d)">{{ d.title }}</button>
              <div class="text-xs text-secondary-500">{{ $t("securone.event.doc_sent_with", { n: d.currentVersion }) }}</div>
            </div>
          </div>

          <VoiceBox v-if="meInfo.canJoinVoice" :eventId="id" :participants="voiceParticipants" :ended="resolved" @joined="load(true)" />

          <button v-if="meInfo.canResolve" class="w-full rounded bg-secondary-50 border border-secondary-200 text-secondary-700 text-sm py-3 hover:bg-secondary-100 focus:outline-none" @click="openResolve">{{ $t("securone.event.resolve") }}</button>
        </div>
      </div>

      <SoNote tone="muted" icon="lock">{{ $t("securone.event.never_alarm_centre") }}</SoNote>
      </div>

      <!-- the chat: always open on the desktop -->
      <div class="xl:col-span-2 xl:sticky" style="top: 1rem">
        <div class="flex items-center justify-between mb-2 px-1">
          <h3 class="text-xs font-semibold text-secondary-700 uppercase tracking-wide"><BaseIcon icon="comment" class="mr-2" />{{ $t("securone.chat.title") }}</h3>
          <span v-if="messageCount" class="text-xs font-mono text-secondary-500">{{ messageCount }}</span>
        </div>
        <EventChat :event="event" :messages="messages" :meId="meId" :readOnly="!meInfo.canChat" :sending="sending" @send="sendChat" />
      </div>
      </div>
      <ResolveModal ref="resolve" @resolved="onResolved" />
    </div>

    <!-- ================= resolved (D-41): the log ================= -->
    <div v-else-if="event" class="flex flex-col gap-5">
      <div class="flex items-center justify-between">
        <router-link :to="{ name: 'securone_events' }" class="text-sm text-securdia-blue hover:underline"><BaseIcon icon="chevron-left" class="mr-1" />{{ $t("securone.event.back_to_events") }}</router-link>
        <span class="text-xs text-secondary-400">{{ event.departmentName }}</span>
      </div>

      <AlarmHeader :event="event" :label="$t('securone.event.resolved_label')" :subtitle="$t('securone.events.sent_by', { name: event.raisedByName })" :instructionLabel="$t('securone.event.instruction_was')">
        <div class="flex flex-wrap gap-8 mt-4">
          <div><div class="text-xs font-semibold uppercase tracking-wider text-gray-500">{{ $t("securone.event.ran_for") }}</div><div class="font-mono text-lg text-gray-800">{{ durationWords }}</div></div>
          <div><div class="text-xs font-semibold uppercase tracking-wider text-gray-500">{{ $t("securone.event.recipients") }}</div><div class="font-mono text-lg text-gray-800">{{ event.recipientCount }}<span v-if="event.smsRecipientCount" class="text-sm text-gray-500"> + {{ event.smsRecipientCount }} SMS</span></div></div>
          <div><div class="text-xs font-semibold uppercase tracking-wider text-gray-500">{{ $t("securone.ack.acknowledged") }}</div><div class="font-mono text-lg text-gray-800">{{ event.acknowledgedCount }}</div></div>
          <div><div class="text-xs font-semibold uppercase tracking-wider text-gray-500">{{ $t("securone.common.groups") }}</div><div class="text-sm text-gray-800 mt-1">{{ groups.map((g) => g.name).join(", ") || "—" }}</div></div>
        </div>
      </AlarmHeader>

      <div class="flex items-start gap-3 sd-card px-5 py-4">
        <BaseIcon icon="lock" class="text-secondary-500 mt-1" />
        <div>
          <div class="font-semibold text-gray-800">{{ $t("securone.event.resolved_by_at", { name: event.resolvedByName, time: clock(event.resolvedAt) }) }}</div>
          <div class="text-xs text-gray-500 mt-1">{{ event.resolvedWithCode ? $t("securone.event.code_was_used") : $t("securone.event.code_not_used") }}</div>
        </div>
      </div>

      <h2 class="text-xs font-semibold text-secondary-700 uppercase tracking-wide">{{ $t("securone.event.recipients") }}</h2>
      <AckList :acknowledgements="ackRows" :recipientCount="event.recipientCount" />

      <DocumentRows :documents="documents" :meta="$t('securone.event.doc_went_out')" />

      <h2 class="text-xs font-semibold text-secondary-700 uppercase tracking-wide">{{ $t("securone.event.timeline") }}</h2>
      <div class="sd-card px-5 py-2">
        <div v-for="(row, i) in timeline" :key="i" class="flex gap-4 py-2 border-b border-secondary-100 last:border-0 text-sm">
          <span class="font-mono text-xs text-gray-400 w-14 flex-shrink-0 pt-0.5">{{ clock(row.t) }}</span>
          <span class="text-gray-700" v-html="row.html"></span>
        </div>
        <div v-if="!timeline.length" class="py-4 text-sm text-gray-400">—</div>
      </div>

      <h2 class="text-xs font-semibold text-secondary-700 uppercase tracking-wide">{{ $t("securone.event.chat") }}</h2>
      <EventChat :event="event" :messages="messages" :meId="meId" readOnly />
    </div>
  </div>
</template>

<script>
import SoNote from "./components/SoNote.vue";
import AlarmHeader from "./components/AlarmHeader.vue";
import AckList from "./components/AckList.vue";
import EventChat from "./components/EventChat.vue";
import DocumentRows from "./components/DocumentRows.vue";
import VoiceBox from "./components/VoiceBox.vue";
import ResolveModal from "./components/ResolveModal.vue";
import api from "./securone.api";
import * as socket from "./securone.socket";
import * as notifications from "./securone.notifications";
import { clock, errorMessage, escapeHtml, tickerMixin } from "./securone.helpers";
import { POLL } from "./securone.constants";

// One route, two surfaces: the running event (sender D-37 / recipient D-38,
// with acknowledge, voice, chat and resolve for those the server allows) and
// the resolved log (D-41). Live: the socket pings when anything on the event
// changes and the view refetches; while active it also polls, at the rate
// securone.constants picks from whether the socket is up.

export default {
  name: "SecurOneEventDetail",
  components: { SoNote, AlarmHeader, AckList, EventChat, DocumentRows, VoiceBox, ResolveModal },
  mixins: [tickerMixin],
  data() {
    return {
      loading: true,
      notFound: false,
      event: null,
      groups: [],
      acknowledgements: [],
      messages: [],
      logs: [],
      documents: [],
      meInfo: { isSender: false, isRecipient: false, ack: null, canJoinVoice: false, canAcknowledge: false, canChat: false, canResolve: false },
      showAcks: false,
      acking: false,
      sending: false,
      unsubscribe: [],
      poll: null,
      refreshTimer: null,
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    me() {
      return this.$store.getters["securone/me"];
    },
    meId() {
      return this.me ? String(this.me._id) : "";
    },
    resolved() {
      return this.event && this.event.status === "resolved";
    },
    myAck() {
      return this.meInfo.ack;
    },
    acknowledged() {
      return !!(this.myAck && this.myAck.state === "acknowledged");
    },
    ackRows() {
      return this.acknowledgements.map((a) => ({ ...a, unitNames: a.departmentNames }));
    },
    ackPreview() {
      const names = this.acknowledgements.filter((a) => a.state === "acknowledged").map((a) => a.name);
      if (!names.length) return this.$t("securone.event.none_yet");
      const shown = names.slice(0, 2).join(" · ");
      return names.length > 2 ? `${shown} · ${this.$t("securone.event.more", { n: names.length - 2 })}` : shown;
    },
    messageCount() {
      return this.messages.filter((m) => m.kind === "message").length;
    },
    voiceParticipants() {
      return Array.from(new Set(this.logs.filter((l) => l.action === "voice_joined").map((l) => l.actorName).filter(Boolean)));
    },
    durationWords() {
      if (!this.event || !this.event.resolvedAt) return "";
      const minutes = Math.round((new Date(this.event.resolvedAt) - new Date(this.event.raisedAt || this.event.createdAt)) / 60000);
      return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
    },
    timeline() {
      const rows = [];
      const bold = (s) => `<b class="font-semibold">${escapeHtml(s)}</b>`;
      this.logs.forEach((l) => {
        if (l.action === "raised") rows.push({ t: l.createdAt, html: `${bold(l.actorName)} ${this.$t("securone.timeline.started")}` });
        else if (l.action === "acknowledged") rows.push({ t: l.createdAt, html: `${bold(l.actorName)} ${this.$t("securone.timeline.acknowledged")}` });
        else if (l.action === "voice_joined") rows.push({ t: l.createdAt, html: `${bold(l.actorName)} ${this.$t("securone.timeline.joined_voice")}` });
        else if (l.action === "access_code_rejected") rows.push({ t: l.createdAt, html: `${bold(l.actorName)} ${this.$t("securone.timeline.code_rejected")}` });
        else if (l.action === "resolved") rows.push({ t: l.createdAt, html: `${bold(l.actorName)} ${l.detail && l.detail.withCode ? this.$t("securone.timeline.resolved_with_code") : this.$t("securone.timeline.resolved")}` });
        else if (l.action === "sms_dispatched") rows.push({ t: l.createdAt, html: this.$t("securone.timeline.sms_sent", { n: (l.detail && l.detail.recipients) || 0 }) });
      });
      this.acknowledgements.filter((a) => a.seenAt && a.state !== "acknowledged").forEach((a) => rows.push({ t: a.seenAt, html: `${bold(a.name)} ${this.$t("securone.timeline.seen")}` }));
      this.messages.filter((m) => m.kind === "message").forEach((m) => rows.push({ t: m.createdAt, html: `${bold(m.senderName)} ${this.$t("securone.timeline.wrote")} “${escapeHtml(m.body)}”` }));
      return rows.sort((a, b) => new Date(a.t) - new Date(b.t));
    },
  },
  methods: {
    clock,
    async load(quiet = false) {
      if (!quiet) this.loading = true;
      try {
        const data = await api.event(this.id);
        this.event = data.event;
        this.groups = data.groups;
        this.acknowledgements = data.acknowledgements;
        this.messages = data.messages;
        this.logs = data.logs;
        this.documents = data.documents;
        this.meInfo = { ...this.meInfo, ...(data.me || {}) };
        this.notFound = false;
        this.schedulePoll();
      } catch (error) {
        if (error.response && error.response.status === 404) this.notFound = true;
        else if (!quiet) this.handleError(error, errorMessage(this, error));
      } finally {
        this.loading = false;
      }
    },
    async acknowledge() {
      this.acking = true;
      try {
        await api.acknowledge(this.id);
        await this.load(true);
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      } finally {
        this.acking = false;
      }
    },
    async sendChat(payload) {
      const { text, file } = typeof payload === "string" ? { text: payload, file: null } : payload || {};
      this.sending = true;
      try {
        const message = await api.chat(this.id, text, file);
        if (!this.messages.some((m) => m._id === message._id)) this.messages.push(message);
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      } finally {
        this.sending = false;
      }
    },
    async openDocument(d) {
      try {
        const file = await api.documentFile(d._id, "current");
        window.open(file.url, "_blank", "noopener");
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      }
    },
    openResolve() {
      this.$refs.resolve.open(this.event);
    },
    onResolved() {
      this.$toasted.show(this.$t("securone.resolve.closed_toast"), { type: "success", theme: "outline", position: "top-right", duration: 5000 });
      this.load();
    },
    // Several pings arrive for one change (event row, ack row, log row): coalesce them.
    refresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.load(true), 150);
    },
    schedulePoll() {
      clearTimeout(this.poll);
      this.poll = null;
      if (!this.event || this.event.status !== "active") return;
      this.poll = setTimeout(() => this.load(true), socket.isConnected() ? POLL.detailSocket : POLL.detailNoSocket);
    },
    subscribe() {
      socket.joinEvent(this.id);
      this.unsubscribe.push(socket.on("securone.event.changed", (p) => { if (p && String(p.eventId) === String(this.id)) this.refresh(); }));
    },
    unsubscribeAll() {
      this.unsubscribe.forEach((fn) => fn());
      this.unsubscribe = [];
      socket.leaveEvent(this.id);
      clearTimeout(this.poll);
      clearTimeout(this.refreshTimer);
    },
  },
  watch: {
    id(value, previous) {
      this.unsubscribeAll();
      socket.leaveEvent(previous);
      this.showAcks = false;
      this.load();
      this.subscribe();
    },
  },
  created() {
    notifications.markSeen(this.id); // the alarm is being looked at: stop ringing for it
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.events"), "securone_event");
    this.load();
    this.subscribe();
  },
  beforeDestroy() {
    this.unsubscribeAll();
  },
};
</script>
